import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@sagot/ui";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import type { SourceUsage, SourceView } from "../shared/types";
import { useSourceEvents } from "../shared/use-source-events";
import { AccessCard } from "./access-card";
import { ChunksPanel } from "./chunks-panel";
import { DeleteSourceDialog } from "./delete-source-dialog";
import { DetailsCard } from "./details-card";
import { QuestionsPanel, UsagePanel } from "./insight-panels";
import { PreviewPanel } from "./preview-panel";
import { ReplaceFileDialog } from "./replace-file-dialog";
import { SourceHeader } from "./source-header";
import { TimelineCard } from "./timeline-card";
import { useSource, useSourceUsage } from "./use-source-detail";
import { useSourcePermissions } from "./use-source-permissions";
import { VersionsCard } from "./versions-card";

const NOT_FOUND = 404;
/** `?agent=<id>` (set by the Knowledge Base list) keeps the back link on the agent you came from. */
export const FROM_AGENT_PARAM = "agent";

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-7xl space-y-6" aria-hidden="true">
      <div className="flex items-start gap-4">
        <Skeleton className="size-12" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-[320px] max-w-full" />
          <Skeleton className="h-5 w-[240px]" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[22rem_1fr]">
        <div className="space-y-6">
          <Skeleton className="h-[360px]" />
          <Skeleton className="h-[200px]" />
        </div>
        <Skeleton className="h-[560px]" />
      </div>
    </div>
  );
}

function useAgentLinks(usage: SourceUsage | undefined) {
  const { summary } = useWorkspaceContext();
  const [params] = useSearchParams();
  const agentId = params.get(FROM_AGENT_PARAM) ?? usage?.agents[0]?.id ?? null;
  return {
    backHref: agentId ? consoleRoutes.knowledge(summary.slug, agentId) : consoleRoutes.agents(summary.slug),
    playgroundHref: agentId ? consoleRoutes.playground(summary.slug, agentId) : null,
  };
}

function SourceTabs({ source, usage, usagePending, canEdit, playgroundHref }: { source: SourceView; usage: SourceUsage | undefined; usagePending: boolean; canEdit: boolean; playgroundHref: string | null }) {
  const { t } = useTranslation();
  const k = "deployKnowledge.sourceDetail.tabs";
  return (
    <Tabs defaultValue="chunks" className="card min-w-0">
      <TabsList className="overflow-x-auto px-3" aria-label={t(`${k}.label`)}>
        <TabsTrigger value="chunks">
          {t(`${k}.chunks`)} <span className="ml-1 text-fg-subtle">{source.chunk_count}</span>
        </TabsTrigger>
        <TabsTrigger value="preview">{t(`${k}.preview`)}</TabsTrigger>
        <TabsTrigger value="questions" className="whitespace-nowrap">
          {t(`${k}.questions`)}
        </TabsTrigger>
        <TabsTrigger value="usage">{t(`${k}.usage`)}</TabsTrigger>
      </TabsList>
      <TabsContent value="chunks">
        <ChunksPanel sourceId={source.id} total={source.chunk_count} canEdit={canEdit} playgroundHref={playgroundHref} />
      </TabsContent>
      <TabsContent value="preview">
        <PreviewPanel source={source} />
      </TabsContent>
      <TabsContent value="questions">
        <QuestionsPanel usage={usage} />
      </TabsContent>
      <TabsContent value="usage">
        <UsagePanel usage={usage} isPending={usagePending} />
      </TabsContent>
    </Tabs>
  );
}

function LoadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const k = "deployKnowledge.sourceDetail";
  if (isPlanGateError(error)) return <UpgradeCard feature={t("nav.knowledge")} />;
  if (isApiError(error) && error.status === NOT_FOUND) {
    const back = (
      <Button asChild variant="secondary">
        <AppLink href={consoleRoutes.agents(summary.slug)}>
          <Icon name="arrow-left" />
          {t(`${k}.back`)}
        </AppLink>
      </Button>
    );
    return <EmptyState icon="file" title={t(`${k}.notFoundTitle`)} description={t(`${k}.notFoundBody`)} action={back} />;
  }
  return <ErrorState title={t(`${k}.loadError`)} description={t("deployKnowledge.loadErrorBody")} onRetry={onRetry} retryLabel={t("common.retry")} />;
}

/** `/w/:ws/knowledge/:sourceId` — tenant/knowledge-detail.html. */
export function SourceDetailPage() {
  const { t } = useTranslation();
  const sourceId = useParams().sourceId ?? "";
  const live = useSourceEvents();
  const source = useSource(sourceId, live === "live");
  const usage = useSourceUsage(sourceId);
  const permissions = useSourcePermissions();
  const links = useAgentLinks(usage.data);
  const [dialog, setDialog] = useState<"replace" | "delete" | null>(null);

  if (source.isPending) return <PageSkeleton />;
  if (source.isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="sr-only">{t("deployKnowledge.sourceDetail.title")}</h1>
        <div className="card">
          <LoadFailure error={source.error} onRetry={() => void source.refetch()} />
        </div>
      </div>
    );
  }
  const data = source.data;
  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SourceHeader source={data} usage={usage.data} backHref={links.backHref} permissions={permissions} onReplace={() => { setDialog("replace"); }} onDelete={() => { setDialog("delete"); }} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[22rem_1fr]">
        <div className="min-w-0 space-y-6">
          <DetailsCard source={data} />
          <AccessCard key={`${data.id}:${data.audiences.join(",")}`} source={data} usage={usage.data} canEdit={permissions.edit} />
          <TimelineCard source={data} live={live} />
          <VersionsCard sourceId={data.id} canEdit={permissions.edit} />
        </div>
        <SourceTabs source={data} usage={usage.data} usagePending={usage.isPending} canEdit={permissions.edit} playgroundHref={links.playgroundHref} />
      </div>
      {dialog === "replace" ? <ReplaceFileDialog source={data} onOpenChange={close} /> : null}
      {dialog === "delete" ? <DeleteSourceDialog source={data} usage={usage.data} afterDeleteHref={links.backHref} onOpenChange={close} /> : null}
    </div>
  );
}
